import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Badge,
  Table,
  Modal,
  Alert,
  Spinner,
  InputGroup,
  Dropdown,
  DropdownButton,
  Pagination,
  Nav,
  Tab,
} from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import { CSVLink } from "react-csv";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

export default function OrderManagement() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState("orders");
  const [locationPage, setLocationPage] = useState(1);
  const [spenderPage, setSpenderPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Order details states
  const [orderUser, setOrderUser] = useState(null);
  const [orderEvent, setOrderEvent] = useState(null);

  // Analytics states
  const [orderAnalytics, setOrderAnalytics] = useState({
    totalOrders: 0,
    totalTransactions: 0,
    totalRevenue: 0,
    completedOrders: 0,
    pendingOrders: 0,
    refundedOrders: 0,
    totalRefundedAmount: 0,
    cancelledOrders: 0,
    ordersByStatus: {},
    revenueByStatus: {},
    orderTrend: [],
    topLocations: [],
    topSpenders: []
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    try {
      const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        navigate('/');
        return;
      }
      
      const adminData = adminDoc.data();
      if (!adminData.roles || !adminData.roles.includes('management')) {
        navigate('/');
        return;
      }
      
      // User has management role, load orders
      loadOrders();
    } catch (err) {
      console.error('Error checking admin access:', err);
      navigate('/');
    }
  };

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, filterStatus]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const ordersSnapshot = await fires.collection("orders").orderBy("timestamp", "desc").get();
      const ordersData = [];
      
      for (const orderDoc of ordersSnapshot.docs) {
        const orderData = orderDoc.data();
        ordersData.push({
          id: orderDoc.id,
          ...orderData,
          createdAt: orderData.timestamp?.toDate?.() || new Date(1970, 0, 1),
        });
      }
      
      setOrders(ordersData);
      calculateOrderAnalytics(ordersData);
    } catch (err) {
      setError("Failed to load orders: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateOrderAnalytics = (ordersData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Calculate orders by status
    const ordersByStatus = {};
    const revenueByStatus = {};
    let totalTransactions = 0;
    let totalRevenue = 0;
    let completedOrders = 0;
    let pendingOrders = 0;
    let refundedOrders = 0;
    let cancelledOrders = 0;
    let totalRefundedAmount = 0;

    ordersData.forEach(order => {
      const status = order.status || "pending";
      const amount = order.amount || 0;
      const amountRefunded = order.amountRefunded || 0;
      
      ordersByStatus[status] = (ordersByStatus[status] || 0) + 1;
      revenueByStatus[status] = (revenueByStatus[status] || 0) + amount;
      totalTransactions += amount;
      totalRefundedAmount += amountRefunded;

      if (status === "completed") completedOrders++;
      else if (status === "pending") pendingOrders++;
      else if (status === "refunded") refundedOrders++;
      else if (status === "cancelled") cancelledOrders++;
    });

    // Calculate revenue as 3.5% of total transactions
    totalRevenue = totalTransactions * 0.035;

    // Calculate order trend (last 6 months)
    const orderTrend = [];
    for (let i = 179; i >= 0; i--) { // 6 months = ~180 days
      const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
      const nextDate = new Date(date.getTime() + 24 * 60 * 60 * 1000);
      const ordersOnDate = ordersData.filter(order => {
        const orderTimestamp = order.createdAt;
        const fallbackDate = new Date(1970, 0, 1);
        return orderTimestamp && orderTimestamp >= date && orderTimestamp < nextDate && orderTimestamp.getTime() !== fallbackDate.getTime();
      });
      
      const totalAmount = ordersOnDate.reduce((sum, order) => sum + (order.amount || 0), 0);
      
      orderTrend.push({
        date: date.toISOString().split('T')[0],
        count: ordersOnDate.length,
        amount: totalAmount
      });
    }

    // Calculate top locations
    const locationMap = new Map();
    ordersData.forEach(order => {
      if (order.viewData?.city && order.viewData?.region) {
        const location = `${order.viewData.city}, ${order.viewData.region}`;
        const existing = locationMap.get(location) || { location, orderCount: 0, totalAmount: 0 };
        existing.orderCount++;
        existing.totalAmount += order.amount || 0;
        locationMap.set(location, existing);
      }
    });
    const topLocations = Array.from(locationMap.values())
      .sort((a, b) => b.orderCount - a.orderCount)
      .slice(0, 20);

    // Calculate top spenders
    const spenderMap = new Map();
    ordersData.forEach(order => {
      if (order.userEmail) {
        const existing = spenderMap.get(order.userEmail) || { 
          email: order.userEmail, 
          userUID: order.userUID || null,
          orderCount: 0, 
          totalAmount: 0,
          uniqueEvents: new Set()
        };
        existing.orderCount++;
        existing.totalAmount += order.amount || 0;
        if (order.eventId) {
          existing.uniqueEvents.add(order.eventId);
        }
        // Keep the first userUID found for this email
        if (!existing.userUID && order.userUID) {
          existing.userUID = order.userUID;
        }
        spenderMap.set(order.userEmail, existing);
      }
    });
    
    // Convert Set to count and sort by unique events first, then by order count
    const topSpenders = Array.from(spenderMap.values())
      .map(spender => ({
        ...spender,
        uniqueEventCount: spender.uniqueEvents.size
      }))
      .sort((a, b) => {
        // First sort by unique events (descending) - prioritize event diversity
        if (b.uniqueEventCount !== a.uniqueEventCount) {
          return b.uniqueEventCount - a.uniqueEventCount;
        }
        // If unique events are equal, sort by order count (descending)
        return b.orderCount - a.orderCount;
      })
      .slice(0, 20);

    setOrderAnalytics({
      totalOrders: ordersData.length,
      totalTransactions,
      totalRevenue,
      completedOrders,
      pendingOrders,
      refundedOrders,
      totalRefundedAmount,
      cancelledOrders,
      ordersByStatus,
      revenueByStatus,
      orderTrend,
      topLocations,
      topSpenders
    });
  };

  const filterOrders = () => {
    let filtered = orders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.uniqueId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.eventId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter(order => order.status === filterStatus);
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const loadOrderDetails = async (orderId) => {
    try {
      // Load user details if we have userEmail
      if (selectedOrder?.userEmail) {
        const userSnapshot = await fires
          .collection("users")
          .where("email", "==", selectedOrder.userEmail)
          .limit(1)
          .get();
        
        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          setOrderUser({
            id: userSnapshot.docs[0].id,
            ...userData
          });
        }
      }

      // Load event details if we have eventId
      if (selectedOrder?.eventId) {
        const eventDoc = await fires.collection("events").doc(selectedOrder.eventId).get();
        if (eventDoc.exists) {
          setOrderEvent({
            id: eventDoc.id,
            ...eventDoc.data()
          });
        }
      }
    } catch (err) {
      console.error("Failed to load order details:", err);
    }
  };

  const handleOrderClick = async (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
    await loadOrderDetails(order.id);
  };

  const exportOrdersToCSV = () => {
    return filteredOrders.map(order => ({
      "Order ID": order.uniqueId || "N/A",
      "Event": order.title || "N/A",
      "User Email": order.userEmail || "N/A",
      "Amount": order.amount || 0,
      "Status": order.status || "N/A",
      "Event ID": order.eventId || "N/A",
      "Created": order.createdAt?.toLocaleString() || "N/A",
      "Campaign ID": order.viewData?.campaignId || "N/A",
      "Country": order.viewData?.country || "N/A",
      "City": order.viewData?.city || "N/A",
      "Region": order.viewData?.region || "N/A",
      "Timezone": order.viewData?.timezone || "N/A"
    }));
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: "success",
      pending: "warning",
      processing: "info",
      cancelled: "danger",
      failed: "secondary",
    };
    return <Badge bg={variants[status] || "secondary"}>{status || "pending"}</Badge>;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown";
    
    const ua = userAgent.toLowerCase();
    let device = "Desktop";
    let browser = "Unknown";
    let os = "Unknown";
    
    // Detect device type
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone') || ua.includes('ipad')) {
      device = "Mobile";
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      device = "Tablet";
    }
    
    // Detect browser
    if (ua.includes('chrome')) browser = "Chrome";
    else if (ua.includes('firefox')) browser = "Firefox";
    else if (ua.includes('safari') && !ua.includes('chrome')) browser = "Safari";
    else if (ua.includes('edge')) browser = "Edge";
    else if (ua.includes('opera')) browser = "Opera";
    
    // Detect OS
    if (ua.includes('windows')) os = "Windows";
    else if (ua.includes('mac os')) os = "macOS";
    else if (ua.includes('iphone') || ua.includes('ipad')) os = "iOS";
    else if (ua.includes('android')) os = "Android";
    else if (ua.includes('linux')) os = "Linux";
    
    return `${device} - ${browser} on ${os}`;
  };

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Card className="mb-4" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
      }}>
        <Card.Body className="text-white">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-0">
                <i className="fas fa-shopping-cart me-2"></i>
                Order Management
              </h2>
              <p className="mb-0 opacity-75">Manage and view all order information, analytics, and details</p>
            </Col>
            <Col xs="auto">
              <Button
                variant="light"
                onClick={loadOrders}
                className="me-2"
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </Button>
              <CSVLink
                data={exportOrdersToCSV()}
                filename={`orders-${new Date().toISOString().split('T')[0]}.csv`}
                className="btn btn-light"
              >
                <i className="fas fa-download me-1"></i>
                Export CSV
              </CSVLink>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Analytics Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center h-100" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            minHeight: "120px"
          }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h3 className="mb-2">{orderAnalytics.totalOrders}</h3>
              <p className="mb-0">Total Orders</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            color: "white",
            minHeight: "120px"
          }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h3 className="mb-2">${orderAnalytics.totalTransactions.toFixed(2)}</h3>
              <p className="mb-0">Total Transactions</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            minHeight: "120px"
          }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h3 className="mb-2">${orderAnalytics.totalRevenue.toFixed(2)}</h3>
              <p className="mb-0">Platform Revenue</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
            color: "white",
            minHeight: "120px"
          }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h3 className="mb-2">{orderAnalytics.refundedOrders}</h3>
              <p className="mb-0">Refunded</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
            color: "white",
            minHeight: "120px"
          }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h3 className="mb-2">${orderAnalytics.totalRefundedAmount.toFixed(2)}</h3>
              <p className="mb-0">Refunded Amount</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
            color: "white",
            minHeight: "120px"
          }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h3 className="mb-2">{orderAnalytics.cancelledOrders}</h3>
              <p className="mb-0">Cancelled</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Trend Chart */}
      <Row className="mb-4">
        <Col md={12}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Order Trend (Last 6 Months)</h6>
              <div style={{ height: "400px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={orderAnalytics.orderTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      interval="month"
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                      }}
                    />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Order Count', angle: -90, position: 'insideLeft', fontSize: 12 }}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right"
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Amount ($)', angle: 90, position: 'insideRight', fontSize: 12 }}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.9)',
                              color: 'white',
                              padding: '10px',
                              border: '1px solid #ccc',
                              borderRadius: '5px'
                            }}>
                              <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
                              <p style={{ margin: '5px 0 0 0', color: '#8884d8' }}>
                                Orders: {payload[0]?.value}
                              </p>
                              <p style={{ margin: '5px 0 0 0', color: '#82ca9d' }}>
                                Amount: ${payload[1]?.value?.toFixed(2)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="count" 
                      fill="#8884d8" 
                      name="Order Count"
                      radius={[2, 2, 0, 0]}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#82ca9d" 
                      strokeWidth={2}
                      name="Order Amount"
                      dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Locations and Top Spenders */}
      <Row className="mb-4">
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Top Locations</h6>
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Location</th>
                      <th className="text-center">Orders</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderAnalytics.topLocations.length > 0 ? (
                      orderAnalytics.topLocations
                        .slice((locationPage - 1) * itemsPerPage, locationPage * itemsPerPage)
                        .map((location, index) => (
                          <tr key={(locationPage - 1) * itemsPerPage + index}>
                            <td className="text-muted">{(locationPage - 1) * itemsPerPage + index + 1}</td>
                            <td>
                              <Badge bg="info" className="fs-7">
                                {location.location}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="success" className="fs-7">
                                {location.orderCount}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <span className="fw-bold text-success">
                                ${location.totalAmount.toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          No location data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {orderAnalytics.topLocations.length > itemsPerPage && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Showing {((locationPage - 1) * itemsPerPage) + 1} - {Math.min(locationPage * itemsPerPage, orderAnalytics.topLocations.length)} of {orderAnalytics.topLocations.length} locations
                  </small>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={locationPage === 1}
                      onClick={() => setLocationPage(locationPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={locationPage >= Math.ceil(orderAnalytics.topLocations.length / itemsPerPage)}
                      onClick={() => setLocationPage(locationPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Top Users</h6>
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Email</th>
                      <th className="text-center">Orders</th>
                      <th className="text-center">Events</th>
                      <th className="text-end">Total Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderAnalytics.topSpenders.length > 0 ? (
                      orderAnalytics.topSpenders
                        .slice((spenderPage - 1) * itemsPerPage, spenderPage * itemsPerPage)
                        .map((spender, index) => (
                          <tr key={(spenderPage - 1) * itemsPerPage + index}>
                            <td className="text-muted">{(spenderPage - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            <div className="small">
                              {spender.userUID ? (
                                <Link 
                                  to={`/user/${spender.userUID}`}
                                  className="fw-bold text-decoration-none"
                                  style={{ color: '#007bff' }}
                                >
                                  {spender.email}
                                </Link>
                              ) : (
                                <div className="fw-bold">{spender.email}</div>
                              )}
                            </div>
                          </td>
                          <td className="text-center">
                            <Badge bg="warning" className="fs-7">
                              {spender.orderCount}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="info" className="fs-7">
                              {spender.uniqueEventCount}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <span className="fw-bold text-primary">
                              ${spender.totalAmount.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No spender data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {orderAnalytics.topSpenders.length > itemsPerPage && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Showing {((spenderPage - 1) * itemsPerPage) + 1} - {Math.min(spenderPage * itemsPerPage, orderAnalytics.topSpenders.length)} of {orderAnalytics.topSpenders.length} users
                  </small>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={spenderPage === 1}
                      onClick={() => setSpenderPage(spenderPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={spenderPage >= Math.ceil(orderAnalytics.topSpenders.length / itemsPerPage)}
                      onClick={() => setSpenderPage(spenderPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4" style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Search Orders</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by order ID, event title, user email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">Status Filter</Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">Results</Form.Label>
                <div className="form-control-plaintext">
                  {filteredOrders.length} orders found
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Orders Table */}
      <Card style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Table responsive hover className="mb-0">
            <thead style={{
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
            }}>
              <tr>
                <th>Order ID</th>
                <th>Event</th>
                <th>User Email</th>
                <th>Amount</th>
                <th>Status</th>
                <th>View Source</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order) => (
                <tr key={order.id} style={{ cursor: "pointer" }} onClick={() => handleOrderClick(order)}>
                  <td>
                    <Link 
                      to={`/order/${order.uniqueId}`}
                      className="text-decoration-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {order.uniqueId || "N/A"}
                    </Link>
                  </td>
                  <td>{order.title || "N/A"}</td>
                  <td>{order.userEmail || "N/A"}</td>
                  <td className="fw-bold text-success">${order.amount || 0}</td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>
                    {order.viewData ? (
                      <div>
                        <Badge bg="info" className="mb-1">
                          {order.viewData.city || "N/A"}, {order.viewData.region || "N/A"}
                        </Badge>
                        <div className="small text-muted">
                          {order.viewData.campaignId && `Campaign: ${order.viewData.campaignId}`}
                          {order.viewData.country && ` • ${order.viewData.country}`}
                          {order.viewData.timezone && ` • ${order.viewData.timezone}`}
                        </div>
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td>{order.createdAt?.toLocaleDateString() || "N/A"}</td>
                  <td>
                    <Link 
                      to={`/order/${order.uniqueId}`}
                      className="btn btn-sm btn-outline-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First 
                  onClick={() => paginate(1)} 
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => paginate(currentPage - 1)} 
                  disabled={currentPage === 1}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => paginate(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => paginate(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => paginate(totalPages)} 
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Order Details Modal */}
      <Modal 
        show={showOrderModal} 
        onHide={() => setShowOrderModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Row>
                <Col md={3}>
                  <Nav variant="pills" className="flex-column">
                    <Nav.Item>
                      <Nav.Link eventKey="overview">Overview</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="user">User Details</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="event">Event Details</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="analytics">View Analytics</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
                <Col md={9}>
                  <Tab.Content>
                    <Tab.Pane eventKey="overview">
                      <Card>
                        <Card.Body>
                          <h6>Order Information</h6>
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td><strong>Order ID:</strong></td>
                                <td>{selectedOrder.uniqueId || "N/A"}</td>
                              </tr>
                              <tr>
                                <td><strong>Event:</strong></td>
                                <td>{selectedOrder.title || "N/A"}</td>
                              </tr>
                              <tr>
                                <td><strong>Amount:</strong></td>
                                <td className="fw-bold text-success">${selectedOrder.amount || 0}</td>
                              </tr>
                              <tr>
                                <td><strong>Status:</strong></td>
                                <td>{getStatusBadge(selectedOrder.status)}</td>
                              </tr>
                              <tr>
                                <td><strong>User Email:</strong></td>
                                <td>{selectedOrder.userEmail || "N/A"}</td>
                              </tr>
                              <tr>
                                <td><strong>Event ID:</strong></td>
                                <td>{selectedOrder.eventId || "N/A"}</td>
                              </tr>
                              <tr>
                                <td><strong>Created:</strong></td>
                                <td>{selectedOrder.createdAt?.toLocaleString() || "N/A"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="user">
                      <Card>
                        <Card.Body>
                          <h6>User Information</h6>
                          {orderUser ? (
                            <Table borderless>
                              <tbody>
                                <tr>
                                  <td><strong>Name:</strong></td>
                                  <td>{(orderUser.firstName || "") + " " + (orderUser.lastName || "")}</td>
                                </tr>
                                <tr>
                                  <td><strong>Email:</strong></td>
                                  <td>{orderUser.email || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Phone:</strong></td>
                                  <td>{orderUser.phone || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Role:</strong></td>
                                  <td>{orderUser.orgId ? "org admin" : (orderUser.role || "user")}</td>
                                </tr>
                                <tr>
                                  <td><strong>Status:</strong></td>
                                  <td>{orderUser.status || "active"}</td>
                                </tr>
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info">User information not available.</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="event">
                      <Card>
                        <Card.Body>
                          <h6>Event Information</h6>
                          {orderEvent ? (
                            <Table borderless>
                              <tbody>
                                <tr>
                                  <td><strong>Event Title:</strong></td>
                                  <td>{orderEvent.title || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Description:</strong></td>
                                  <td>{orderEvent.description || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Start Date:</strong></td>
                                  <td>{orderEvent.startDate?.toDate?.()?.toLocaleString() || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>End Date:</strong></td>
                                  <td>{orderEvent.endDate?.toDate?.()?.toLocaleString() || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Status:</strong></td>
                                  <td>{orderEvent.status || "N/A"}</td>
                                </tr>
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info">Event information not available.</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="analytics">
                      <Card>
                        <Card.Body>
                          <h6>View Analytics</h6>
                          {selectedOrder.viewData ? (
                            <Table borderless>
                              <tbody>
                                <tr>
                                  <td><strong>Source:</strong></td>
                                  <td>{selectedOrder.viewData.source || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Campaign ID:</strong></td>
                                  <td>{selectedOrder.viewData.campaignId || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Location:</strong></td>
                                  <td>
                                    {selectedOrder.viewData.city || "N/A"}, {selectedOrder.viewData.region || "N/A"}, {selectedOrder.viewData.country || "N/A"}
                                  </td>
                                </tr>
                                <tr>
                                  <td><strong>Timezone:</strong></td>
                                  <td>{selectedOrder.viewData.timezone || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>User Agent:</strong></td>
                                  <td>{parseUserAgent(selectedOrder.viewData.fromUA)}</td>
                                </tr>
                                <tr>
                                  <td><strong>IP Address:</strong></td>
                                  <td>{selectedOrder.viewData.currentIP || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>URL:</strong></td>
                                  <td>{selectedOrder.viewData.url || "N/A"}</td>
                                </tr>
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info">View analytics not available.</Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
} 